import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const api = useMemo(() => axios.create({ baseURL: API, withCredentials: true }), []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /me — AuthCallback handles it.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const otpSend = useCallback(async (phone) => {
    const res = await api.post('/auth/otp/send', { phone });
    return res.data;
  }, [api]);

  const otpVerify = useCallback(async (phone, code) => {
    const res = await api.post('/auth/otp/verify', { phone, code });
    setUser(res.data.user);
    return res.data;
  }, [api]);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch (err) { console.error('Logout error:', err); }
    setUser(null);
  }, [api]);

  const value = useMemo(() => ({
    user, loading, api,
    loginWithGoogle, otpSend, otpVerify, logout,
    setUser, checkAuth,
  }), [user, loading, api, loginWithGoogle, otpSend, otpVerify, logout, checkAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
